import { Grid, Skeleton } from '@mui/material';
import { Formik } from 'formik';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import {
	CerebroDrawer,
	CerebroForm,
	CerebroHeading,
	CerebroHierarchyFolderTree,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroStyledBox,
} from '../../shared/components';
import { useApi } from '../../shared/context';
import { QueryKeys, useDataRoomFolderHierarchyQuery } from '../../shared/hooks';
import { FormikUtils } from '../../shared/utils';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { getApiResponseData } from '../../shared/utils/axios';
import { DataRoomDocument } from '../../shared/models';

interface MoveDocumentFormProps {
	dataRoomGuid: string | undefined;
	document: DataRoomDocument | null | undefined;
	close: () => void;
}

export const MoveDocumentForm: React.FC<MoveDocumentFormProps> = ({ dataRoomGuid, document, close }) => {
	const api = useApi();
	const client = useQueryClient();
	const snackbar = useSnackbar();
	const foldersQuery = useDataRoomFolderHierarchyQuery(dataRoomGuid);

	const moveDocumentMutation = useMutation(
		async ({ values }: MoveDocumentFormMutationVariables) => {
			const documentPatch = {
				name: document?.name,
				documentType: document?.documentType,
				folderGuid: values.folderGuid,
				isVisibleToAll: document?.isVisibleToAll,
				dataRoomUsers: document?.dataRoomUsers as number[],
				updateAction: 'Move',
			};

			const { data, error } = await getApiResponseData<DataRoomDocument>(
				api.patch(CerebroApiEndpoints.Documents.Update(dataRoomGuid, document?.guid), documentPatch)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.Documents.Root(dataRoomGuid, document?.folderGuid));
				snackbar.enqueueSnackbar('Data room document was edited.', { variant: 'success' });
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, { variant: 'error' });
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={document != null} onClose={() => close()} PaperProps={{ sx: { width: '700px' } }}>
			<CerebroHeading>{document?.name}</CerebroHeading>
			<Formik
				initialValues={{
					folderGuid: document?.folderGuid,
				}}
				onSubmit={FormikUtils.MutateOnSubmit(moveDocumentMutation.mutate)}
			>
				{({ isSubmitting, handleSubmit, values, setFieldValue }) => {
					return (
						<CerebroForm onSubmit={handleSubmit}>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								<Grid item xs={8}>
									<CerebroLabel>Data Room Folder</CerebroLabel>
									{foldersQuery.isLoading && <Skeleton height={60} />}
									{foldersQuery.isSuccess && (
										<CerebroStyledBox alignitems="left" sx={{ marginTop: 1 }}>
											<CerebroHierarchyFolderTree
												folder={foldersQuery.data}
												selectedGuid={values.folderGuid}
												hidePrivateFolders={false}
												onSelected={value => {
													values.folderGuid = value;
													setFieldValue('folderGuid', value);
												}}
											/>
										</CerebroStyledBox>
									)}
								</Grid>

								<Grid item xs={8}>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Save
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

type MoveDocumentFormMutationVariables = FormikUtils.MutateOnSubmitVariables<MoveDocumentFormValues>;
interface MoveDocumentFormValues {
	folderGuid: string | undefined;
}
